/**
 * The user-facing quality levels for compile-time lighting subdivision, and how they
 * map onto the compiler's numeric `maxEdgeLength`.
 *
 * The firmware shades per vertex, so a Point or Spot only contributes where a vertex
 * is. Subdivision buys lighting sample points; the levels here are the shapes of that
 * trade-off, measured on chair22 (284 source triangles, longest edge 20.8):
 *
 *   Off     284 triangles, 17.0 KB  - the original mesh
 *   Low     306 triangles, 17.7 KB  - splits only the oversized quads
 *   Medium  686 triangles, 27.4 KB  - recommended; lighting is converged here
 *   High   1940 triangles, 58.8 KB  - diminishing returns beyond this
 *
 * The level is a property of the MODEL RESOURCE, not of a widget instance: subdivision
 * changes the compiled H3D0 file itself, so every widget pointing at that resource sees
 * the same geometry.
 *
 * `maxEdgeLength` stays numeric on the compiler side so an Advanced setting can expose
 * it directly later without changing the compiler contract.
 */

export const LIGHTING_QUALITY_OPTIONS = Object.freeze([
  { value: 'off', label: 'Off', detail: 'Original mesh' },
  { value: 'low', label: 'Low', detail: 'Minimal extra geometry' },
  { value: 'medium', label: 'Medium', detail: 'Recommended for ESP32-P4' },
  { value: 'high', label: 'High', detail: 'Maximum practical quality' },
])

/**
 * Applied to newly imported resources only.
 *
 * Deliberately NOT the fallback for a resource that has no `lightingQuality`: an
 * existing project must keep compiling exactly as it did, so absence resolves to 'off'
 * (see resolveLightingQuality) rather than being silently upgraded to this.
 */
export const DEFAULT_LIGHTING_QUALITY = 'medium'

/** What a resource saved before this setting existed has always compiled as. */
export const LEGACY_LIGHTING_QUALITY = 'off'

const MAX_EDGE_LENGTH_BY_QUALITY = Object.freeze({
  low: 10,
  medium: 2.5,
  high: 1.25,
})

export function isLightingQuality(value) {
  return LIGHTING_QUALITY_OPTIONS.some(option => option.value === value)
}

/**
 * The level a resource compiles at. An unset or unrecognised value resolves to the
 * legacy behaviour, never to the new-resource default.
 */
export function resolveLightingQuality(model) {
  const quality = model?.lightingQuality
  return isLightingQuality(quality) ? quality : LEGACY_LIGHTING_QUALITY
}

/** The `options.lightingSubdivision` payload compileModel3dResource() expects. */
export function lightingSubdivisionOptions(quality) {
  const maxEdgeLength = MAX_EDGE_LENGTH_BY_QUALITY[quality]
  if (!maxEdgeLength) return { enabled: false }
  return { enabled: true, maxEdgeLength }
}

/** Convenience for callers holding a resource rather than a level. */
export function lightingSubdivisionOptionsForModel(model) {
  return lightingSubdivisionOptions(resolveLightingQuality(model))
}
